import React, { FunctionComponent, useEffect, useState } from 'react'
import styles from './ArchivePage.module.sass'
import ProjectFilters from '../../projects/ProjectList/ProjectFilters/ProjectFilters'
import { useRootDispatch, useRootSelector } from '../../../../utils/hooks'
import { pick } from '../../../../utils/utils'
import { archiveActions } from '../../../../redux/pages/archive'
import { flow } from 'fp-ts/function'
import { ProjectStatus, projectStatusToColor } from '../../../../responses/projects'
import { statusContext } from 'ts/constants/contexts'
import { RequestStatus } from '../../../../responses/response'
import TopBarProgress from 'react-topbar-progress-indicator'
import Helmet from 'react-helmet'
import InfiniteScroll from '../../../etc/scroll/InfiniteScroll'
import { projectActions } from '../../../../redux/data/project/project-actions'
import ProjectLine from '../../projects/ProjectList/ProjectLine/ProjectLine'
import { LoadingSkeleton } from '../../../etc/loading-components'
import { isEmpty, isNonEmpty } from 'fp-ts/es6/Array'
import SidePanel from '../../../etc/SidePanel/SidePanel'
import { userRenderer } from '../../../../utils/renderers'
import { AnimatePresence } from 'framer-motion'
import { User } from '../../../../responses/users'
import Button, { ButtonStyle } from '../../../controls/Button/Button'
import ArchiveIcon from 'files/archive/archive.svg'
import StreamerIcon from 'files/archive/streamer.svg'
import { Colors } from '../../../../styles'
import { Checkbox } from '../../../controls/Checkbox/Checkbox'
import * as A from 'fp-ts/Array'
import { eqId } from '../../../../utils/entity-utils'
import { projectsSelector } from '../../../../utils/selectors'
import { enumIs } from '../../../../utils/enum-utils'
import { HasAllPermissions } from '../../../etc/HasPermission'
import { PERMISSIONS } from '../../../../constants/permissions'
import { archiveStatusFilters } from './config'


const isSelected = enumIs()([ProjectStatus.TO_ARCHIVE, ProjectStatus.CLOSED])

const ArchivePage: FunctionComponent = () => {
    const dispatch = useRootDispatch()
    const {
        filters,
        total,
        status,
        projects,
        selected
    } = useRootSelector(state => pick(state.pages.archive, 'filters', 'total', 'status', 'projects', 'selected'))

    const allSelected = useRootSelector(flow(
        projectsSelector(projects),
        A.filter(project => isSelected(project.status_id)),
        toArchive => toArchive.length === selected.length,
    ))


    const [users, setUsers] = useState<User[]>([])

    useEffect(() => {
        dispatch(projectActions.archive.init(filters))
    }, [dispatch, filters])

    return <div className={styles.page}>
        {
            status === RequestStatus.FETCHING && <TopBarProgress/>
        }
        <Helmet>
            <title>Архив</title>
        </Helmet>
        <ProjectFilters
            value={filters}
            total={total}
            statusFilters={archiveStatusFilters}
            onChange={flow(archiveActions.updateFilters, dispatch)}
            onClear={flow(archiveActions.clearFilters, dispatch)}
            toggle={<Checkbox
                color={Colors.grey}
                value={allSelected}
                onChange={() => dispatch(archiveActions.selectedProjects.toggleAll())}
            />}
            actions={<>
                <Button
                   buttonStyle={ButtonStyle.BORDERED}
                   color={Colors.purpleDark}
                   onClick={() => dispatch(archiveActions.startReserving())}
                >
                    <StreamerIcon/> На стример
                </Button>
                <Button
                    buttonStyle={ButtonStyle.BORDERED}
                    disabled={selected.length === 0}
                    title={selected.length === 0 ? 'Выберите проекты для архивации' : undefined}
                    color={Colors.purpleMedium}
                    onClick={() => dispatch(archiveActions.startArchiving(selected))}
                >
                    <ArchiveIcon/> Архивировать
                </Button>
            </>}
        />
        <statusContext.Provider value={isNonEmpty(projects) ? RequestStatus.SUCCESS : status}>
            <InfiniteScroll
                className={styles.list}
                onReach={() => dispatch(projectActions.archive.more())}
            >
                <LoadingSkeleton className={styles.table} count={8} height={64}>
                    {
                        isEmpty(projects) && <div className={styles.empty}>Проекты не найдены</div>
                    }
                    {
                        projects.map(projectId => {
                            const open = A.elem(eqId)(projectId)(selected)
                            return <ProjectLine
                                key={projectId}
                                archiveMode
                                projectId={projectId}
                                onUsersClick={setUsers}
                                control={project => <HasAllPermissions
                                    condition={[ProjectStatus.TO_ARCHIVE, ProjectStatus.CLOSED].includes(project.status_id)}
                                    permissions={[PERMISSIONS.PROJECT.ARCHIVE]}
                                >
                                    <Checkbox
                                        color={projectStatusToColor(project.status_id)}
                                        value={open}
                                        onChange={() => dispatch(archiveActions.selectedProjects.toggle(projectId))}
                                    />
                                </HasAllPermissions>}
                            />
                        })
                    }
                </LoadingSkeleton>

            </InfiniteScroll>
            <AnimatePresence>
                {
                    isNonEmpty(users) && <SidePanel
                        onClose={() => setUsers([])}
                        className={styles.userContainer}
                        header={'Участники проекта'}
                    >
                        {
                            users.map(userRenderer)
                        }
                    </SidePanel>
                }
            </AnimatePresence>
        </statusContext.Provider>
    </div>
}

export default ArchivePage